/**
 * Destinations analytics tab.
 *
 * Pulled into its own file from `pages/Analytics.js` because the visual-
 * edits Babel plugin in this repo crashes ("Maximum call stack size
 * exceeded") on the combined size of all three tabs + their helpers in one
 * module. Keep this file flat — no deep ternary nesting inside JSX.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import FYSelect, { CURRENT_FY } from '@/components/FYSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PRIMARY = '#0BA888';

const fmtINRCompact = (n) => {
  const v = Number(n || 0);
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(0)}K`;
  return `₹${Math.round(v)}`;
};

// Bar lightens progressively by rank — keeps the dominant bar visually
// anchored without losing readability on the long tail.
const _barColor = (rank) => `hsl(170 88% ${24 + Math.min(rank * 6, 50)}%)`;

const DestinationBar = ({ row, widthPct, rank, onClick }) => {
  const bgColor = rank === 0 ? PRIMARY : _barColor(rank);
  const inside = widthPct >= 22; // ₹amount + N pax need ~22% width to read.
  return (
    <button
      type="button"
      onClick={() => onClick(row.destination)}
      className="w-full flex items-center gap-3 group text-left hover:opacity-90 active:opacity-75 transition-opacity"
      title={`Click to filter Trips by ${row.destination}`}
      data-testid={`dest-bar-${row.destination}`}
    >
      <div className="w-[110px] text-right text-xs text-slate-700 truncate">
        {row.destination}
      </div>
      <div className="flex-1 relative h-7 rounded-md bg-slate-50">
        <div
          className="h-full rounded-md flex items-center px-2 transition-all"
          style={{ width: `${widthPct}%`, backgroundColor: bgColor }}
        >
          {inside ? (
            <span className="text-[11px] font-medium text-white whitespace-nowrap">
              {fmtINRCompact(row.revenue)} · {row.pax} pax
            </span>
          ) : null}
        </div>
        {!inside ? (
          <span
            className="absolute top-0 h-7 flex items-center text-[11px] font-medium whitespace-nowrap pl-2"
            style={{ left: `${widthPct}%`, color: '#6B7280' }}
          >
            {fmtINRCompact(row.revenue)} · {row.pax} pax
          </span>
        ) : null}
      </div>
    </button>
  );
};

const HeadlineCard = ({ label, value, subValue, testid }) => (
  <Card className="p-4" data-testid={testid}>
    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-lg font-semibold text-slate-900 mt-1 truncate">{value}</div>
    {subValue ? <div className="text-[10px] text-slate-400 mt-0.5">{subValue}</div> : null}
  </Card>
);

const DestinationsTab = () => {
  const navigate = useNavigate();
  const [fy, setFy] = useState(CURRENT_FY);
  const [data, setData] = useState({ rows: [], topDestination: null, highestMargin: null, fastestGrowing: null });
  const [loading, setLoading] = useState(false);

  // Drill-down — clicking a bar deep-links into the Trips list with the
  // destination pre-applied as a search term (existing backend search
  // matches destination via regex). One-click drill-down turns the chart
  // from "report" into an operating tool.
  const drillDown = (destination) => {
    navigate(`/quotes?search=${encodeURIComponent(destination)}&from=analytics`);
  };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/analytics/destinations?fy=${fy}`)
      .then((res) => { if (alive) setData(res.data); })
      .catch(() => toast.error('Failed to load destinations'))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [fy]);

  const maxRevenue = useMemo(
    () => Math.max(1, ...data.rows.map((r) => r.revenue || 0)),
    [data.rows],
  );

  const fastestGrowingSub = (data.fastestGrowingPct === null || data.fastestGrowingPct === undefined)
    ? null
    : `+${data.fastestGrowingPct}%`;

  // Hoist to identifiers — the visual-edits Babel plugin recursively chokes
  // on `data.rows.map(...)` in JSX (analyzeMemberExpression ↔
  // getArrayIterationContext ping-pong). Identifiers go through a
  // skipArrayContext-aware path and terminate.
  const rows = data.rows;
  const topDestination = data.topDestination;
  const highestMargin = data.highestMargin;
  const fastestGrowing = data.fastestGrowing;

  return (
    <div className="space-y-5" data-testid="destinations-tab">
      <div className="flex justify-end">
        <FYSelect value={fy} onValueChange={setFy} testid="destinations-fy-select" />
      </div>

      <Card className="p-5" data-testid="destinations-chart">
        {loading ? <ChartLoading /> : null}
        {!loading && rows.length === 0 ? <ChartEmpty fy={fy} /> : null}
        {!loading && rows.length > 0 ? (
          <div className="space-y-2">
            {rows.map((r, idx) => (
              <DestinationBar key={r.destination} row={r} widthPct={(r.revenue / maxRevenue) * 100} rank={idx} onClick={drillDown} />
            ))}
          </div>
        ) : null}
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <HeadlineCard testid="dest-top-destination" label="Top destination" value={topDestination || '—'} />
        <HeadlineCard testid="dest-highest-margin" label="Highest margin" value={highestMargin || '—'} />
        <HeadlineCard testid="dest-fastest-growing" label="Fastest growing (YoY)" value={fastestGrowing || '—'} subValue={fastestGrowingSub} />
      </div>
    </div>
  );
};

const ChartLoading = () => <div className="py-10 text-center text-sm text-slate-400">Loading…</div>;
const ChartEmpty = ({ fy }) => <div className="py-10 text-center text-sm text-slate-400">No confirmed trips in FY {fy}.</div>;

export default DestinationsTab;
